import {HttpResponse} from '@angular/common/http';
import {Injectable} from '@angular/core';

import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';

import {
  CriterionList,
  CriterionListType,
  CurrentRights,
  EmpPopulationCriterion,
  PossibleRights,
  RepPopulationCriterion
} from './user-rights.interfaces';

import {REQUEST_OPTIONS} from '../../app.constants';
import {ApiService} from '../../shared/api';

@Injectable({
  providedIn: 'root'
})
export class UserRightsService {

  public criterionOptions: CriterionList = {
    empCriterionList: {
      key: CriterionListType.EMP_CRITERION_LIST,
      title: 'SETTINGS.population_access_criteria',
      criterionList: [],
      saveKey: 'empCriterionIds',
      possibleRights: null,
      criterionObject: {},
      fields: [
        {
          title: 'SETTINGS.zone_name',
          name: 'zoneName'
        },
        {
          title: 'FORMS.Value',
          name: 'value'
        },
        {
          title: 'SETTINGS.access_mode',
          name: 'access'
        }
      ],
      errorMessage: 'SETTINGS.population_access_criteria_disabled'
    },
    repCriterionList: {
      key: CriterionListType.REP_CRITERION_LIST,
      title: 'SETTINGS.entity_access_criteria',
      criterionList: [],
      saveKey: 'repCriterionIds',
      criterionObject: {},
      possibleRights: null,
      fields: [
        {
          title: 'SETTINGS.entity_type',
          name: 'entityType'
        },
        {
          title: 'SETTINGS.zone_name',
          name: 'zoneName'
        },
        {
          title: 'FORMS.Value',
          name: 'value'
        },
        {
          title: 'SETTINGS.access_mode',
          name: 'access'
        }
      ],
      errorMessage: 'SETTINGS.entity_access_criteria_disabled'
    }
  };

  constructor(private api: ApiService) {
  }

  public getPossibleRights(id: number): Observable<PossibleRights> {
    return this.api.get(`/authent/api/v1/userRights/getPossibleRights/${id}`);
  }

  public getPossibleRightsByUserId(id: number): Observable<PossibleRights> {
    return this.api.get(`/authent/api/v1/userRights/getPossibleRightsByUserId/${id}`);
  }


  public getUserRights(id: number): Observable<CurrentRights> {
    return this.api.get(`/authent/api/v1/userRights/getCurrentRights/${id}`);
  }

  public getUserRightsByUserId(id: number): Observable<CurrentRights> {
    return this.api.get(`/authent/api/v1/userRights/getCurrentRightsByUserId/${id}`);
  }

  public setUserRights(userId: number,
                       profileName: string,
                       criterionIds: { [key: string]: number[] },
                       ownContractAccess: boolean,
                       additionalProfileNames?: string[]): Observable<HttpResponse<void>> {
    return this.api.post(`/authent/api/v1/userRights/update/${userId}`, null, {
      requestOptions: {
        params: {
          profileName,
          ...criterionIds,
          ownContractAccess,
          ...(additionalProfileNames && {additionalProfileNames})
        }
      }
    });
  }

  public setUserRightsByUserId(userId: number,
                               profileName: string,
                               criterionIds: { [key: string]: number[] },
                               accessEndDate: Date,
                               ownContractAccess: boolean,
                               additionalProfileNames?: string[]): Observable<HttpResponse<void>> {
    const hasAccessEndDate = !!accessEndDate;

    return this.api.post(`/authent/api/v1/userRights/updateByUserId/${userId}`, null, {
      requestOptions: {
        params: {
          profileName,
          ...criterionIds,
          ...(hasAccessEndDate && {accessEndDate}),
          ownContractAccess,
          ...(additionalProfileNames && {additionalProfileNames})
        }
      }
    });
  }

  public createPopulationAccessCriterion(payload: EmpPopulationCriterion): Observable<HttpResponse<void>> {
    return this.api.post(`/authent/api/v1/emp-population-criterion`, payload);
  }

  public updatePopulationAccessCriterion(id: number, payload: EmpPopulationCriterion): Observable<HttpResponse<void>> {
    return this.api.put(`/authent/api/v1/emp-population-criterion/update/${id}`, payload);
  }

  public fetchEmpPopulationCriterionByZoneName(zoneName: string, page: number, perPage: number): Observable<{
    data: EmpPopulationCriterion[],
    total: number
  }> {
    const filter = `(zoneName==${zoneName})`;
    const queryParams = `search=${filter}&order-by=-id&page=${page}&per-page=${perPage}`;

    return this.api.get(`/authent/api/v1/emp-population-criterion?${queryParams}`, REQUEST_OPTIONS.OBSERVE.RESPONSE)
      .pipe(
        map(response => ({
          total: parseInt(response.headers.get('x-total-count'), 10) || 0,
          data: response.status === 204 ? [] : response.body
        }))
      );
  }

  public removePopulationAccessCriterion(id: number): Observable<Response> {
    return this.api.delete(`/authent/api/v1/emp-population-criterion/${id}`);
  }


  public fetchRepPopulationCriterionByEntityTypes(entityTypes: string[], page: number, perPage: number): Observable<{
    data: RepPopulationCriterion[],
    total: number
  }> {
    const filter = `entityType=in=(${(Array.from(entityTypes).join(','))})`;
    const queryParams = `search=${filter}&order-by=-id&page=${page}&per-page=${perPage}`;
    return this.api.get(`/authent/api/v1/rep-population-criterion?${queryParams}`, REQUEST_OPTIONS.OBSERVE.RESPONSE)
     .pipe(
      map(response => ({
        total: parseInt(response.headers.get('x-total-count'), 10) || 0,
        data: response.status === 204 ? [] : response.body
      }))
     );
  }

  public createRepAccessCriterion(payload: RepPopulationCriterion): Observable<HttpResponse<void>> {
    return this.api.post(`/authent/api/v1/rep-population-criterion`, payload);
  }

  public updateRepAccessCriterion(id: number, payload: RepPopulationCriterion): Observable<HttpResponse<void>> {
    return this.api.put(`/authent/api/v1/rep-population-criterion/update/${id}`, payload);
  }

  public removeRepAccessCriterion(id: number): Observable<Response> {
    return this.api.delete(`/authent/api/v1/rep-population-criterion/${id}`);
  }

  public transferUserAccess(userId: number,
                            queryParams: string): Observable<HttpResponse<void>> {
    return this.api.silent.post(`/authent/api/v1/userRights/${userId}/transferPopulationCriterion?${queryParams}`);
  }

  public transferUserAccessByUserId(userId: number,
                                    queryParams: string): Observable<HttpResponse<void>> {
    return this.api.silent.post(`/authent/api/v1/userRights/${userId}/transferPopulationCriterionByUserId?${queryParams}`);
  }

  public getAllowOwnContractFlag(): Observable<boolean> {
    return this.api.authent.get('/userRights/own-contract-visible');
  }
}
